/**
 * Extract tooth references from patient notes so the dental chart can
 * reflect what the notes actually say.
 *
 * Two sources per note:
 * 1. The structured `teeth_involved` array (when staff filled it in).
 * 2. Free-text mentions in title/content — e.g. "שן 36", "שיניים 11, 21",
 *    "tooth 46" — which today silently never reach the tooth map.
 *
 * FDI notation: two digits, quadrant 1-4 + position 1-8 (11..48).
 */

const TOOTH_KEYWORD_RE = /(?:שן|שיניים|tooth|teeth)[^0-9]{0,12}((?:[1-4][1-8])(?:\s*(?:[,،]|ו-?|-|–|\/|\s)\s*[1-4][1-8])*)/gi;

const isValidTooth = (n) => {
  if (!Number.isInteger(n)) return false;
  const quadrant = Math.floor(n / 10);
  const position = n % 10;
  return quadrant >= 1 && quadrant <= 4 && position >= 1 && position <= 8;
};

export const extractTeethFromText = (text) => {
  const teeth = new Set();
  if (!text) return teeth;
  const re = new RegExp(TOOTH_KEYWORD_RE.source, 'gi');
  let match;
  while ((match = re.exec(text)) !== null) {
    const numbers = match[1].match(/[1-4][1-8]/g) || [];
    numbers.forEach((s) => {
      const n = parseInt(s, 10);
      if (isValidTooth(n)) teeth.add(n);
    });
  }
  return teeth;
};

/**
 * @param {Array} notes — patient notes ({id, title, content, teeth_involved})
 * @returns {Map<number, Array<{id, title}>>} tooth number -> notes mentioning it
 */
export const extractTeethFromNotes = (notes) => {
  const map = new Map();
  (notes || []).forEach((note) => {
    const teeth = new Set();
    (note.teeth_involved || []).forEach((t) => {
      const n = parseInt(t, 10);
      if (isValidTooth(n)) teeth.add(n);
    });
    extractTeethFromText(`${note.title || ''} ${note.content || ''}`).forEach((n) =>
      teeth.add(n)
    );
    teeth.forEach((tooth) => {
      if (!map.has(tooth)) map.set(tooth, []);
      map.get(tooth).push({ id: note.id, title: note.title || note.note_type || '' });
    });
  });
  return map;
};
